import { Check } from "lucide-react";
import { useSetup } from "../SetupContext";
import type { PipelineStep, SubStep } from "./wizardTypes";

/* ────────────────────────────────────────────────────────────── */
/*  PIPELINE STEPPER (top-level 7 steps)                         */
/* ────────────────────────────────────────────────────────────── */
const PIPELINE_STEPS = [
  "Project Details",
  "Data Ingestion",
  "Data Cleaning",
  "Supplier Consolidation",
  "Spend Classification",
  "Publish Dashboard",
  "Governance & QA",
];

export function PipelineStepper7({
  active,
  totalSubStep,
  startFromDataIngestion = false,
}: {
  active: PipelineStep;
  totalSubStep: number;
  startFromDataIngestion?: boolean;
}) {
  const visibleSteps = startFromDataIngestion ? PIPELINE_STEPS.slice(1) : PIPELINE_STEPS;
  const normalizedActive = startFromDataIngestion
    ? (Math.max(2, active) - 1)
    : active;

  const completedSteps = normalizedActive - 1;
  const stepsComplete = normalizedActive - 1;
  const step1Fraction = !startFromDataIngestion && active === 1 ? (totalSubStep - 1) / 4 : 1;
  const rawPct = (stepsComplete + ((!startFromDataIngestion && active === 1) ? step1Fraction : 1)) / visibleSteps.length;
  const pct = Math.round(rawPct * 100);

  return (
    <div className="pipeline7-stepper">
      <div className="pipeline7-stepper__header">
        <span className="pipeline7-stepper__title">Pipeline Progress</span>
        <span className="pipeline7-stepper__status">
          {completedSteps} of {visibleSteps.length} steps complete
          <span className="pipeline7-stepper__pct">{pct}%</span>
        </span>
      </div>

      <div className="pipeline7-steps">
        {visibleSteps.map((label, i) => {
          const displayStep = i + 1;
          const done = displayStep < normalizedActive;
          const curr = displayStep === normalizedActive;
          return (
            <div
              key={label}
              className={`pipeline7-step${curr ? " is-active" : ""}${done ? " is-done" : ""}`}
            >
              <div
                className="pipeline7-step__circle"
                aria-current={curr ? "step" : undefined}
              >
                {done ? <Check size={12} /> : displayStep}
              </div>
              <div className="pipeline7-step__label">{label}</div>
            </div>
          );
        })}
      </div>

      <div className="pipeline7-progress">
        <div
          className="pipeline7-progress__fill"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

/* ── Sub-step tabs for Step 1 ────────────────────────────────── */
const SUB_STEP_LABELS = ["Project Details", "Taxonomy", "Workflow", "Users & Access"];

export function SubStepTabs({ active }: { active: SubStep }) {
  return (
    <div className="setup-steps bg-white" role="tablist">
      {SUB_STEP_LABELS.map((label, i) => {
        const n = (i + 1) as SubStep;
        const done = n < active;
        const curr = n === active;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center" }}>
            {i > 0 && <span className="setup-step__connector" aria-hidden="true" />}
            <div
              className={`setup-step${curr ? " is-active" : done ? " is-done" : ""}`}
              role="tab"
              aria-selected={curr}
            >
              <div className="setup-step__num">{done ? <Check size={11} /> : n}</div>
              <span className="setup-step__label">{label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Summary Sidebar ─────────────────────────────────────────── */
export function SummarySidebar() {
  const { form, completion } = useSetup();
  const rows = [
    { key: "Project Name",  val: form.projectName  || "—" },
    { key: "Business Unit", val: form.businessUnit || "—" },
    { key: "Category",      val: form.category     || "—" },
    { key: "Region",        val: form.region       || "—" },
    { key: "Budget",        val: form.budget       ? `$${form.budget}` : "—" },
    { key: "Taxonomy",      val: form.taxonomy     || "—", accent: true },
    { key: "Automation",    val: `${form.automationLevel}%`, accent: true },
    { key: "Team Members",  val: `${form.teamMemberIds.length} user${form.teamMemberIds.length !== 1 ? "s" : ""}`, accent: true },
    { key: "Completion",    val: `${completion}%`, accent: true },
  ];
  return (
    <aside className="setup-summary" aria-label="Project summary">
      <div className="setup-summary__title">Setup summary</div>
      <div className="setup-summary__sub">What you've configured so far.</div>
      <div className="setup-summary__table">
        {rows.map((r) => (
          <div key={r.key} className="setup-summary__row">
            <span className="setup-summary__key">{r.key}</span>
            <span className={`setup-summary__val${r.accent ? " setup-summary__val--accent" : ""}`}>{r.val}</span>
          </div>
        ))}
      </div>
    </aside>
  );
}
